// Hook para el chat de "Describí el fondo" — pequeño cliente SSE contra
// la misma edge function `chat-agent` que el chat principal de V2.
//
// Diferencias vs useV2Chat:
//   • Sistema prompt acotado a director de arte para fondos.
//   • Una sola tool (`propose_scenes`) que devuelve sugerencias
//     estructuradas con un opcional título y un prompt.
//   • Sin multi-round (max 1 vuelta del agente por mensaje del user). Si
//     el agente llama a la tool, sintetizamos un tool_result genérico
//     en el próximo turno del user para que Anthropic acepte el array
//     de messages.

import { useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { SUPABASE_ANON_KEY, AGENT_CHAT_URL } from '../../config';

export interface DescribeSuggestion {
  titulo?: string;
  prompt: string;
}

export interface DescribeChatMessage {
  role: 'user' | 'assistant';
  /** Texto plano. Para el assistant, lo que el modelo "dice" antes/después
   *  de invocar la tool. */
  text: string;
  /** Sugerencias estructuradas extraídas del tool_use. */
  suggestions?: DescribeSuggestion[];
}

// ─── Tool definition ─────────────────────────────────────────────────────

const PROPOSE_SCENES_TOOL = {
  name: 'propose_scenes',
  description:
    'Propone descripciones de fondos/escenarios para fotos de producto. Devolvé 3 ideas distintas al sugerir, o 1 al mejorar/refinar.',
  input_schema: {
    type: 'object',
    properties: {
      intro: {
        type: 'string',
        description:
          'Frase corta presentando las sugerencias (ej: "Acá tenés 3 ideas:" o "Mejoré tu idea:"). Opcional.',
      },
      scenes: {
        type: 'array',
        description:
          'Lista de escenarios propuestos. 3 al sugerir desde cero, 1 al mejorar/refinar.',
        items: {
          type: 'object',
          properties: {
            titulo: {
              type: 'string',
              description:
                'Nombre corto del escenario (2-4 palabras). Opcional al mejorar/refinar.',
            },
            prompt: {
              type: 'string',
              description:
                'Descripción del fondo, 1-2 oraciones. Solo describe el entorno — sin mencionar productos ni modelos.',
            },
          },
          required: ['prompt'],
        },
      },
    },
    required: ['scenes'],
  },
};

// ─── System prompt builder ───────────────────────────────────────────────

function buildDescribeSystem(args: {
  rubro: string | null;
  productNames: string[];
}): string {
  const rubroLine = args.rubro
    ? `Rubro del usuario: **${args.rubro}**.`
    : 'No tenés información del rubro del usuario.';
  const catalog =
    args.productNames.length > 0
      ? `Productos seleccionados para esta foto: ${args.productNames
          .map((n) => `"${n}"`)
          .join(', ')}.`
      : 'No hay productos seleccionados.';
  return `Sos un Director de Arte experto en sesiones de producto fotorrealistas para redes (Instagram, e-commerce). Tu trabajo es ayudar al usuario a diseñar el FONDO/ESCENARIO de la imagen — nunca el producto ni la modelo.

${rubroLine}
${catalog}

Reglas:
- Cada descripción es de 1-2 oraciones máximo. Solo el entorno/escenario.
- NUNCA menciones el producto, la modelo, ni la composición de la foto.
- Estética cinematográfica: priorizá entornos orgánicos y reales, con luz natural interesante y texturas tangibles. Evitá estudios fotográficos planos a menos que el rubro lo pida.
- No uses placeholders ni variables tipo {color} o [marca].

Cuando el usuario pida "sugerime ideas" (sin descripción previa), proponé 3 escenarios DISTINTOS entre sí usando la tool propose_scenes con scenes.length === 3.

Cuando el usuario te dé una descripción y pida "ayudame a mejorar mi idea", devolvé UNA versión mejorada usando propose_scenes con scenes.length === 1. Conservá la esencia de su idea pero afiná detalles concretos (iluminación, texturas, atmósfera, paleta).

Cuando el usuario te pida ajustes adicionales sobre lo último propuesto ("más oscuras", "que sea de noche", etc), aplicá su feedback y devolvé UNA versión refinada con propose_scenes.

SIEMPRE llamá a la tool propose_scenes. Podés acompañar con un texto breve antes (1 oración natural en español rioplatense) pero la tool es obligatoria.`;
}

// ─── Hook ────────────────────────────────────────────────────────────────

interface UseDescribeChatArgs {
  /** Catálogo de productos (nombres) que se va a fotografíar. */
  productNames: string[];
  /** Rubro de la empresa — para que el agente entienda el contexto. */
  rubro: string | null;
}

export interface UseDescribeChatReturn {
  messages: DescribeChatMessage[];
  streaming: boolean;
  start: (initialPrompt: string) => Promise<void>;
  sendFollowUp: (text: string) => Promise<void>;
  reset: () => void;
}

/** Anthropic-shaped messages (para el array que mandamos a la edge fn). */
interface ApiMessage {
  role: 'user' | 'assistant';
  content:
    | string
    | Array<
        | { type: 'text'; text: string }
        | { type: 'tool_use'; id: string; name: string; input: any }
        | {
            type: 'tool_result';
            tool_use_id: string;
            content: string;
          }
      >;
}

export function useDescribeChat(
  args: UseDescribeChatArgs,
): UseDescribeChatReturn {
  const [messages, setMessages] = useState<DescribeChatMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  // El "shadow history" en formato API — incluye los tool_use blocks
  // que Anthropic necesita para saber qué proponer después. Lo
  // mantenemos paralelo a `messages` (UI) para no contaminar las
  // burbujas con tool_use crudo.
  const apiMessagesRef = useRef<ApiMessage[]>([]);
  // El último tool_use_id pendiente (para sintetizar tool_result en
  // el próximo turno del user).
  const pendingToolUseIdRef = useRef<string | null>(null);
  const generationRef = useRef(0);

  const reset = () => {
    setMessages([]);
    setStreaming(false);
    apiMessagesRef.current = [];
    pendingToolUseIdRef.current = null;
    generationRef.current++;
  };

  const runRound = async () => {
    const token = ++generationRef.current;
    setStreaming(true);
    // Burbuja del assistant que iremos llenando con el texto streameado.
    setMessages((prev) => [...prev, { role: 'assistant', text: '' }]);

    let textSoFar = '';
    let toolUseBlock: {
      id: string;
      name: string;
      _json: string;
      input?: any;
    } | null = null;
    const orderedBlocks: Array<
      | { type: 'text'; text: string }
      | {
          type: 'tool_use';
          id: string;
          name: string;
          input: any;
        }
    > = [];
    const blockMap = new Map<number, any>();

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No session');

      const systemPrompt = buildDescribeSystem({
        rubro: args.rubro,
        productNames: args.productNames,
      });

      const res = await fetch(AGENT_CHAT_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: apiMessagesRef.current,
          system: systemPrompt,
          tools: [PROPOSE_SCENES_TOOL],
        }),
      });
      if (!res.ok || !res.body) {
        throw new Error(`chat-agent error: ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processEvent = (block: string) => {
        const dataLines: string[] = [];
        for (const line of block.split('\n')) {
          if (line.startsWith('data:'))
            dataLines.push(line.slice(5).trimStart());
        }
        if (dataLines.length === 0) return;
        const raw = dataLines.join('\n');
        if (raw === '[DONE]') return;
        let evt: any;
        try {
          evt = JSON.parse(raw);
        } catch {
          return;
        }
        if (evt?.type === 'content_block_start') {
          const idx = evt.index;
          const cb = evt.content_block;
          if (cb?.type === 'tool_use') {
            const tu = { id: cb.id, name: cb.name, _json: '' };
            blockMap.set(idx, { ...tu, kind: 'tool_use' });
            toolUseBlock = tu as any;
          } else if (cb?.type === 'text') {
            blockMap.set(idx, { kind: 'text', text: '' });
          }
        } else if (evt?.type === 'content_block_delta') {
          const idx = evt.index;
          const existing = blockMap.get(idx);
          if (evt.delta?.type === 'text_delta') {
            const piece = evt.delta.text || '';
            textSoFar += piece;
            if (existing?.kind === 'text') existing.text += piece;
            // Empujamos a la UI en tiempo real — solo el texto del
            // assistant; las suggestions llegarán al final cuando el
            // tool_use complete su JSON.
            if (token === generationRef.current) {
              setMessages((prev) => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last && last.role === 'assistant') {
                  next[next.length - 1] = { ...last, text: textSoFar };
                }
                return next;
              });
            }
          } else if (
            evt.delta?.type === 'input_json_delta' &&
            existing?.kind === 'tool_use'
          ) {
            existing._json += evt.delta.partial_json || '';
          }
        } else if (evt?.type === 'content_block_stop') {
          const existing = blockMap.get(evt.index);
          if (existing?.kind === 'tool_use') {
            try {
              existing.input = existing._json
                ? JSON.parse(existing._json)
                : {};
            } catch {
              existing.input = {};
            }
            delete existing._json;
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = buffer.replace(/\r\n/g, '\n');
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          if (block.trim()) processEvent(block);
          boundary = buffer.indexOf('\n\n');
        }
      }
      if (buffer.trim()) processEvent(buffer);

      // Si la generación cambió mientras streameamos (reset), salimos.
      if (token !== generationRef.current) return;

      // Construir los blocks ordenados para guardar en el shadow history.
      [...blockMap.entries()]
        .sort((a, b) => a[0] - b[0])
        .forEach(([, v]) => {
          if (v.kind === 'text' && v.text) {
            orderedBlocks.push({ type: 'text', text: v.text });
          } else if (v.kind === 'tool_use') {
            orderedBlocks.push({
              type: 'tool_use',
              id: v.id,
              name: v.name,
              input: v.input || {},
            });
            pendingToolUseIdRef.current = v.id;
          }
        });

      // Extraer sugerencias del último tool_use de propose_scenes.
      const lastToolUse = orderedBlocks.find(
        (b): b is Extract<typeof b, { type: 'tool_use' }> =>
          b.type === 'tool_use' && b.name === 'propose_scenes',
      );
      const suggestions: DescribeSuggestion[] = lastToolUse?.input?.scenes
        ?.filter((s: any) => s?.prompt)
        ?.map((s: any) => ({
          titulo: typeof s.titulo === 'string' ? s.titulo : undefined,
          prompt: String(s.prompt),
        })) || [];

      // Si la tool incluyó un `intro` y el assistant no streameó texto,
      // usamos el intro como texto de la burbuja.
      const introFromTool: string =
        (!textSoFar && lastToolUse?.input?.intro) || '';

      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = {
            ...last,
            text: textSoFar || introFromTool,
            suggestions: suggestions.length > 0 ? suggestions : undefined,
          };
        }
        return next;
      });

      // Guardar el turno del assistant en el shadow history.
      apiMessagesRef.current.push({
        role: 'assistant',
        content: orderedBlocks,
      });
    } catch (err) {
      console.error('[useDescribeChat] error', err);
      if (token === generationRef.current) {
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              text:
                'No pude conectarme. Probá de nuevo en un momento.',
            };
          }
          return next;
        });
      }
    } finally {
      if (token === generationRef.current) {
        setStreaming(false);
      }
    }
  };

  const start = async (initialPrompt: string) => {
    if (streaming) return;
    reset();
    apiMessagesRef.current = [
      { role: 'user', content: initialPrompt },
    ];
    setMessages([{ role: 'user', text: initialPrompt }]);
    await runRound();
  };

  const sendFollowUp = async (text: string) => {
    if (streaming) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    // Si hay un tool_use pendiente del turno anterior, Anthropic exige
    // un tool_result antes del próximo texto del user. Lo sintetizamos
    // con un OK genérico — el contenido no importa porque el agente lo
    // usa solo como acknowledgement.
    const userBlocks: Array<any> = [];
    if (pendingToolUseIdRef.current) {
      userBlocks.push({
        type: 'tool_result',
        tool_use_id: pendingToolUseIdRef.current,
        content: 'ok',
      });
      pendingToolUseIdRef.current = null;
    }
    userBlocks.push({ type: 'text', text: trimmed });
    apiMessagesRef.current.push({
      role: 'user',
      content: userBlocks.length === 1 ? trimmed : userBlocks,
    });
    setMessages((prev) => [...prev, { role: 'user', text: trimmed }]);
    await runRound();
  };

  return { messages, streaming, start, sendFollowUp, reset };
}
